/**
 * useBabyCare Hook
 *
 * Provides baby care functionality with feature limit checks,
 * realtime sync, and automatic data refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLimits } from '@/hooks/useLimits';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { isValidUUID } from '@/lib/utils';
import {
  BabyCareEntry,
  CreateBabyCareEntry,
  BabyCareStats,
  BabyCareDailySummary,
  CareType,
  createBabyCareEntry,
  getBabyCareEntries,
  updateBabyCareEntry,
  deleteBabyCareEntry,
  endCareSession,
  getBabyCareStats,
  getBabyCareDailySummary,
  getActiveSleepSessions,
  getLastEndedSleep,
  getTimeSinceLastCare,
} from '@/services/babyCareService';
import { createBabyCareActivity } from '@/features/activity-feed/services/activityFeedService';

// Available care types with their labels
export const CARE_TYPES: { type: CareType; label: string; icon: string }[] = [
  { type: 'feeding', label: 'Füttern', icon: '🍼' },
  { type: 'diaper', label: 'Windel', icon: '🧷' },
  { type: 'sleep', label: 'Schlafen', icon: '😴' },
  { type: 'medication', label: 'Medikamente', icon: '💊' },
  { type: 'temperature', label: 'Temperatur', icon: '🌡️' },
  { type: 'notes', label: 'Notizen', icon: '📝' },
];

// Initial number of entries to load
const INITIAL_ENTRIES_LIMIT = 12;
// Number of entries to load on "load more"
const ENTRIES_PAGE_SIZE = 12;

export interface UseBabyCareReturn {
  // Data
  entries: BabyCareEntry[];
  stats: BabyCareStats[];
  dailySummary: BabyCareDailySummary[];
  /** @deprecated Use activeSleepSessions instead for multi-child support */
  activeSleepSession: BabyCareEntry | null;
  /** All active sleep sessions (one per sleeping baby) */
  activeSleepSessions: BabyCareEntry[];
  /**
   * The most recent completed sleep across all babies (MAX(ended_at)).
   * Null if no sleep has ever been ended. Used by the sleep quick-action
   * button to display "time since last wake-up".
   */
  lastEndedSleep: BabyCareEntry | null;

  // State
  isLoading: boolean;
  isCreating: boolean;
  isLoadingMore: boolean;
  hasMoreEntries: boolean;
  error: string | null;

  // Feature availability
  isFeatureEnabled: boolean;
  availableCareTypes: CareType[];
  isCareTypeAvailable: (type: CareType) => boolean;
  historyDaysLimit: number;
  statsMaxDaysLimit: number;

  // Multi-child helpers
  /** Check if a specific baby is currently sleeping */
  isBabyAsleep: (babyIndex: number) => boolean;

  // Actions
  addEntry: (entry: CreateBabyCareEntry) => Promise<BabyCareEntry | null>;
  updateEntry: (id: string, updates: Partial<CreateBabyCareEntry>) => Promise<BabyCareEntry | null>;
  deleteEntry: (id: string) => Promise<boolean>;
  endSleepSession: (id: string, endedAt?: string) => Promise<BabyCareEntry | null>;
  cancelSleepSession: (id: string) => Promise<boolean>;
  loadMoreEntries: () => Promise<void>;
  getTimeSince: (careType: CareType) => Promise<{ minutes: number | null; lastEntry: BabyCareEntry | null }>;
  refetch: () => Promise<void>;
}

export function useBabyCare(): UseBabyCareReturn {
  const { user } = useAuth();
  const { lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { isLimitEnabled, getNumberLimit, isUnlimited } = useLimits();
  const isFeatureEnabled = useFeatureEnabled('baby_care_enabled');

  // In baby mode, use lastCompletedPregnancy
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const rawPregnancyId = isBabyMode ? lastCompletedPregnancy?.id : null;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // State
  const [entries, setEntries] = useState<BabyCareEntry[]>([]);
  const [stats, setStats] = useState<BabyCareStats[]>([]);
  const [dailySummary, setDailySummary] = useState<BabyCareDailySummary[]>([]);
  const [activeSleepSessions, setActiveSleepSessions] = useState<BabyCareEntry[]>([]);
  const [lastEndedSleep, setLastEndedSleep] = useState<BabyCareEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreEntries, setHasMoreEntries] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Feature limits
  const historyDaysLimit = getNumberLimit('baby_care_history_days');
  const statsMaxDaysLimit = getNumberLimit('baby_care_stats_max_days');
  const hasUnlimitedHistory = isUnlimited('baby_care_history_days');

  // Available care types based on limits
  const availableCareTypes: CareType[] = CARE_TYPES
    .filter(({ type }) => isLimitEnabled(`baby_care_${type}`))
    .map(({ type }) => type);

  const isCareTypeAvailable = useCallback(
    (type: CareType): boolean => {
      return isLimitEnabled(`baby_care_${type}`);
    },
    [isLimitEnabled]
  );

  // Fetch entries (initial load with limit)
  const fetchEntries = useCallback(async () => {
    if (!pregnancyId || !isFeatureEnabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Calculate start date based on history limit
      let startDate: string | undefined;
      if (!hasUnlimitedHistory && historyDaysLimit > 0) {
        const start = new Date();
        start.setDate(start.getDate() - historyDaysLimit);
        startDate = start.toISOString();
      }

      // Fetch initial entries (limited)
      const { data, error: fetchError } = await getBabyCareEntries(pregnancyId, {
        startDate,
        limit: INITIAL_ENTRIES_LIMIT + 1, // Fetch one extra to check if there are more
      });

      if (fetchError) {
        setError(fetchError);
        return;
      }

      // Check if there are more entries
      if (data.length > INITIAL_ENTRIES_LIMIT) {
        setHasMoreEntries(true);
        setEntries(data.slice(0, INITIAL_ENTRIES_LIMIT));
      } else {
        setHasMoreEntries(false);
        setEntries(data);
      }
      setCurrentOffset(INITIAL_ENTRIES_LIMIT);
    } catch (err) {
      console.error('Error fetching baby care entries:', err);
      setError('Fehler beim Laden der Einträge');
    } finally {
      setIsLoading(false);
    }
  }, [pregnancyId, isFeatureEnabled, hasUnlimitedHistory, historyDaysLimit]);

  // Load more entries
  const loadMoreEntries = useCallback(async () => {
    if (!pregnancyId || !isFeatureEnabled || isLoadingMore || !hasMoreEntries) {
      return;
    }

    try {
      setIsLoadingMore(true);

      // Calculate start date based on history limit
      let startDate: string | undefined;
      if (!hasUnlimitedHistory && historyDaysLimit > 0) {
        const start = new Date();
        start.setDate(start.getDate() - historyDaysLimit);
        startDate = start.toISOString();
      }

      // Fetch next page of entries
      const { data, error: fetchError } = await getBabyCareEntries(pregnancyId, {
        startDate,
        limit: ENTRIES_PAGE_SIZE + 1, // Fetch one extra to check if there are more
        offset: currentOffset,
      });

      if (fetchError) {
        setError(fetchError);
        return;
      }

      // Check if there are more entries
      if (data.length > ENTRIES_PAGE_SIZE) {
        setHasMoreEntries(true);
        setEntries(prev => [...prev, ...data.slice(0, ENTRIES_PAGE_SIZE)]);
      } else {
        setHasMoreEntries(false);
        setEntries(prev => [...prev, ...data]);
      }
      setCurrentOffset(prev => prev + ENTRIES_PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more baby care entries:', err);
      setError('Fehler beim Laden weiterer Einträge');
    } finally {
      setIsLoadingMore(false);
    }
  }, [pregnancyId, isFeatureEnabled, isLoadingMore, hasMoreEntries, currentOffset, hasUnlimitedHistory, historyDaysLimit]);

  // Fetch stats (24 hours default for quick overview)
  const fetchStats = useCallback(async () => {
    if (!pregnancyId || !isFeatureEnabled) return;

    const { data, error: statsError } = await getBabyCareStats(pregnancyId, 24);
    if (!statsError) {
      setStats(data);
    }
  }, [pregnancyId, isFeatureEnabled]);

  // Fetch daily summary
  const fetchDailySummary = useCallback(async () => {
    if (!pregnancyId || !isFeatureEnabled) return;

    const { data, error: summaryError } = await getBabyCareDailySummary(pregnancyId);
    if (!summaryError) {
      setDailySummary(data);
    }
  }, [pregnancyId, isFeatureEnabled]);

  // Fetch all active sleep sessions (multi-child support)
  const fetchActiveSleepSessions = useCallback(async () => {
    if (!pregnancyId || !isFeatureEnabled) return;

    const { data } = await getActiveSleepSessions(pregnancyId);
    setActiveSleepSessions(data);
  }, [pregnancyId, isFeatureEnabled]);

  // Fetch the most recently ended sleep across all babies
  const fetchLastEndedSleep = useCallback(async () => {
    if (!pregnancyId || !isFeatureEnabled) {
      setLastEndedSleep(null);
      return;
    }
    const { data } = await getLastEndedSleep(pregnancyId);
    setLastEndedSleep(data);
  }, [pregnancyId, isFeatureEnabled]);

  // Check if a specific baby is currently sleeping
  const isBabyAsleep = useCallback(
    (babyIndex: number): boolean => {
      return activeSleepSessions.some(s => s.baby_index === babyIndex);
    },
    [activeSleepSessions]
  );

  // Combined refetch
  const refetch = useCallback(async () => {
    await Promise.all([
      fetchEntries(),
      fetchStats(),
      fetchDailySummary(),
      fetchActiveSleepSessions(),
      fetchLastEndedSleep(),
    ]);
  }, [
    fetchEntries,
    fetchStats,
    fetchDailySummary,
    fetchActiveSleepSessions,
    fetchLastEndedSleep,
  ]);

  // Initial fetch
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Realtime subscription
  useEffect(() => {
    if (!pregnancyId || !isFeatureEnabled) return;

    const channel = supabase
      .channel(`baby-care:${pregnancyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'baby_care_entries',
          filter: `pregnancy_id=eq.${pregnancyId}`,
        },
        () => {
          // Refresh data on any change
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [pregnancyId, isFeatureEnabled, refetch]);

  // Helper to generate activity title based on care type
  const getActivityTitle = (entry: CreateBabyCareEntry): string => {
    switch (entry.care_type) {
      case 'feeding':
        if (entry.feeding_type === 'breast') return 'Gestillt';
        if (entry.feeding_type === 'bottle') return `Flasche: ${entry.feeding_amount_ml || '?'} ml`;
        return 'Brei gefüttert';
      case 'diaper':
        if (entry.diaper_type === 'wet') return 'Windel gewechselt (nass)';
        if (entry.diaper_type === 'dirty') return 'Windel gewechselt (voll)';
        if (entry.diaper_type === 'both') return 'Windel gewechselt (beides)';
        return 'Windel gewechselt (trocken)';
      case 'sleep':
        return entry.ended_at ? 'Schlaf beendet' : 'Schlaf gestartet';
      case 'medication':
        return `Medikament: ${entry.medication_name || 'gegeben'}`;
      case 'temperature':
        return `Temperatur: ${entry.temperature_value || '?'}°C`;
      case 'notes':
        return 'Notiz hinzugefügt';
      default:
        return 'Baby-Pflege';
    }
  };

  // Add entry
  const addEntry = useCallback(
    async (entry: CreateBabyCareEntry): Promise<BabyCareEntry | null> => {
      if (!user || !pregnancyId) return null;

      // Check if care type is available
      if (!isCareTypeAvailable(entry.care_type)) {
        setError(`${entry.care_type} ist in deinem Abo nicht verfügbar`);
        return null;
      }

      setIsCreating(true);
      setError(null);

      try {
        const { data, error: createError } = await createBabyCareEntry(user.id, {
          ...entry,
          pregnancy_id: pregnancyId,
        });

        if (createError) {
          setError(createError);
          return null;
        }

        // Create activity feed entry (non-blocking)
        createBabyCareActivity(user.id, {
          pregnancy_id: pregnancyId,
          care_type: entry.care_type,
          title: getActivityTitle(entry),
          description: entry.notes || undefined,
          metadata: {
            feeding_type: entry.feeding_type,
            feeding_amount_ml: entry.feeding_amount_ml,
            diaper_type: entry.diaper_type,
            medication_name: entry.medication_name,
            temperature_value: entry.temperature_value,
          },
          recorded_at: entry.recorded_at,
        }).catch(err => console.warn('Failed to create activity entry:', err));

        // Refetch to update state
        await refetch();
        return data;
      } finally {
        setIsCreating(false);
      }
    },
    [user, pregnancyId, isCareTypeAvailable, refetch]
  );

  // Update entry
  const updateEntry = useCallback(
    async (id: string, updates: Partial<CreateBabyCareEntry>): Promise<BabyCareEntry | null> => {
      setError(null);

      const { data, error: updateError } = await updateBabyCareEntry(id, updates);

      if (updateError) {
        setError(updateError);
        return null;
      }

      await refetch();
      return data;
    },
    [refetch]
  );

  // Delete entry
  const deleteEntry = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);

      const { success, error: deleteError } = await deleteBabyCareEntry(id);

      if (deleteError) {
        setError(deleteError);
        return false;
      }

      await refetch();
      return success;
    },
    [refetch]
  );

  // End sleep session (with optional custom end time)
  const endSleepSession = useCallback(
    async (id: string, endedAt?: string): Promise<BabyCareEntry | null> => {
      setError(null);

      const { data, error: endError } = await endCareSession(id, endedAt);

      if (endError) {
        setError(endError);
        return null;
      }

      await refetch();
      return data;
    },
    [refetch]
  );

  // Cancel sleep session (delete without logging)
  const cancelSleepSession = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);

      const { success, error: deleteError } = await deleteBabyCareEntry(id);

      if (deleteError) {
        setError(deleteError);
        return false;
      }

      await refetch();
      return success;
    },
    [refetch]
  );

  // Get time since last care of type
  const getTimeSince = useCallback(
    async (careType: CareType): Promise<{ minutes: number | null; lastEntry: BabyCareEntry | null }> => {
      if (!pregnancyId) return { minutes: null, lastEntry: null };

      const { minutes, lastEntry } = await getTimeSinceLastCare(pregnancyId, careType);
      return { minutes, lastEntry };
    },
    [pregnancyId]
  );

  return {
    // Data
    entries,
    stats,
    dailySummary,
    activeSleepSession: activeSleepSessions[0] || null,
    activeSleepSessions,
    lastEndedSleep,

    // State
    isLoading,
    isCreating,
    isLoadingMore,
    hasMoreEntries,
    error,

    // Feature availability
    isFeatureEnabled,
    availableCareTypes,
    isCareTypeAvailable,
    historyDaysLimit,
    statsMaxDaysLimit,

    // Multi-child helpers
    isBabyAsleep,

    // Actions
    addEntry,
    updateEntry,
    deleteEntry,
    endSleepSession,
    cancelSleepSession,
    loadMoreEntries,
    getTimeSince,
    refetch,
  };
}
